import { resolveEventPageTicker } from 'constants/syntheticMarginEvents';

import { upperFirstLetter } from './caseing';
import { getSeriesRelativeUrl, sportSeriesRedirects } from './series';

export enum MarketForm {
    BINARY = 'binary',
    CATEGORICAL = 'categorical',
    NUMERICAL = 'numerical',
    FINANCIAL_UNDERLYING = 'financial-underlying',
}

export const getEventRelativeUrl = (
    seriesTicker: string,
    seriesTitle?: string,
    eventTicker?: string,
    marketTicker?: string,
    /** Preselects the order panel's side alongside the `marketTicker` deep link. */
    orderSide?: 'yes' | 'no',
): string => {
    const baseUrl = getSeriesRelativeUrl(seriesTicker, seriesTitle);
    const query = [marketTicker && `marketTicker=${marketTicker}`, orderSide && `orderSide=${orderSide}`]
        .filter(Boolean)
        .join('&');
    const queryParams = query ? `?${query}` : '';

    // Use slash-based URL structure instead of hash-based
    if (eventTicker) {
        const mappedSeriesTicker = sportSeriesRedirects[seriesTicker] ?? seriesTicker;
        const mappedEventTicker = resolveEventPageTicker(eventTicker).replace(seriesTicker, mappedSeriesTicker);

        return `${baseUrl}/${mappedEventTicker.toLowerCase()}${queryParams}`;
    }

    return `${baseUrl}${queryParams}`;
};

/**
 * Carries a redirect's incoming search params onto `path`. Values already on `path`
 * (e.g. `marketTicker`) are overridden by a forwarded param of the same name.
 */
export const withForwardedSearchParams = (
    path: string,
    params: Record<string, string | string[] | undefined>,
): string => {
    const [pathname, existing] = path.split('?');
    const query = new URLSearchParams(existing);
    for (const [key, value] of Object.entries(params)) {
        if (Array.isArray(value)) value.forEach((item) => query.append(key, item));
        else if (value !== undefined) query.set(key, value);
    }
    const queryString = query.toString();
    return queryString ? `${pathname}?${queryString}` : pathname;
};

/**
 * Derive the event ticker from a Kalshi market ticker by dropping the
 * trailing `-MARKET[-…]` segment. Returns `undefined` for tickers without a
 * hyphen (rare single-market events whose market ticker is the event ticker).
 *
 * Example: `KXPGATOUR-CAC26-CAME` → `KXPGATOUR-CAC26`.
 */
export const deriveEventTickerFromMarketTicker = (marketTicker: string): string | undefined => {
    const i = marketTicker.lastIndexOf('-');
    return i > 0 ? marketTicker.slice(0, i) : undefined;
};

export const getMinimalSubtitle = (subtitle: string) => {
    let minimalSubtitle = subtitle;

    // Some prepositions can be inferred
    if (minimalSubtitle.slice(0, 3) === 'In ' || minimalSubtitle.slice(0, 3) === 'On ') {
        minimalSubtitle = upperFirstLetter(subtitle.slice(3));
    }

    return minimalSubtitle;
};
